import { useMemo, useState, useEffect } from "react";
import throttle from "lodash/throttle";

export type Breakpoint = "xs" | "sm" | "md" | "lg" | "xl";
export type BreakpointConfiguration = Record<Breakpoint, number>;
export type IterableBreakpoints = [Breakpoint, number][];

export const BREAKPOINTS: BreakpointConfiguration = {
  xs: 640,
  sm: 768,
  md: 1024,
  lg: 1280,
  xl: Infinity
};

const getDeviceConfig = (
  iteratableBreakpoints: IterableBreakpoints,
  defaultBreakpoint: Breakpoint = "xl"
): Breakpoint => {
  /* istanbul ignore next */
  if (typeof window === "undefined") {
    return defaultBreakpoint;
  }

  const width = window.innerWidth;

  let previousBreakpointValue = 0;

  for (const [breakpoint, value] of iteratableBreakpoints) {
    if (width >= previousBreakpointValue && width < value) {
      return breakpoint;
    }

    previousBreakpointValue = value;
  }

  return [...iteratableBreakpoints].pop()[0];
};

const useBreakpoint = (
  breakpoints: Partial<BreakpointConfiguration> = BREAKPOINTS
) => {
  const iteratableBreakpoints = useMemo(() => {
    return Object.entries(Object.assign({}, BREAKPOINTS, breakpoints)).sort(
      ([, a], [, b]) => a - b
    ) as [Breakpoint, number][];
  }, [breakpoints]);
  const [breakpoint, set] = useState(() =>
    getDeviceConfig(iteratableBreakpoints)
  );

  useEffect(() => {
    const calcInnerWidth = throttle(function () {
      set(getDeviceConfig(iteratableBreakpoints));
    }, 200);

    window.addEventListener("resize", calcInnerWidth, { passive: true });

    return () => window.removeEventListener("resize", calcInnerWidth);
  }, []);

  return breakpoint;
};

export default useBreakpoint;
